import { RefObject, useEffect } from 'react'

/**
 * The window-level side effects an open modal owns: lock the background from
 * scrolling, move focus into the dialog, and put focus back where it was when
 * the dialog goes away.
 *
 * One brick rather than three copies. `AllForOne_Grades_Grid.html` specifies
 * all three on its grading panel (`document.body.style.overflow = 'hidden'`,
 * `markRight.focus({preventScroll:true})`, and restoring `lastFocus` in
 * `closeGrade`), and the app had none of them in any modal. Inlining it in the
 * one modal being worked on would have left the next modal to re-discover it.
 *
 * Mount/unmount IS open/close for the callers here -- they render the dialog
 * conditionally -- so there is no `open` flag to get out of step with reality.
 * A modal that stays mounted while hidden would need one, and should not use
 * this hook as-is.
 *
 * Restores the PREVIOUS overflow value rather than clearing it, so a nested or
 * concurrent lock is not silently undone. The mockup clears it; clearing is
 * only correct when nothing else could have set it.
 */
export const useModalBehaviour = (initialFocusRef?: RefObject<HTMLElement | null>) => {
	useEffect(() => {
		const previouslyFocused = document.activeElement as HTMLElement | null
		const previousOverflow = document.body.style.overflow

		document.body.style.overflow = 'hidden'
		initialFocusRef?.current?.focus({ preventScroll: true })

		return () => {
			document.body.style.overflow = previousOverflow
			/* isConnected guards the case where the thing that opened the dialog
			   was itself removed while it was open -- focusing a detached node
			   throws nothing but silently drops focus to <body>. */
			if (previouslyFocused?.isConnected) {
				previouslyFocused.focus({ preventScroll: true })
			}
		}
	}, [initialFocusRef])
}
